import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Dropdown from "@shared/v2/Dropdown";
import Button from "@shared/v2/Button";
import TextComboInput from "@shared/v2/TextComboInput";
import { Table, TBody, TD, TH, THead, TR } from "../../Reports/components/BusinessDashboard/Table/Table";
import { Search as SearchIcon, Spinner } from "../../shared/v2/Icomoon";

const Autocomplete = ({ selectedItem = () => {} }) => {
  const [value, setValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [filtered, setFiltered] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (value.length < 3) {
      setFiltered([]);
      setShowDropdown(false);
      selectedItem(null);
      return undefined;
    }

    if (value.includes("http")) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setDebouncedValue(value);
    }, 500);

    return () => clearTimeout(timeout);
  }, [value]);

  useEffect(() => {
    if (debouncedValue) {
      setIsLoading(true);

      axios.get(`/support/ppc_adds_dashboard/autocomplete?term=${value}`).then((response) => {
        setIsLoading(false);
        setFiltered(response.data.data);
        setShowDropdown(true);
        setActiveIndex(-1);
      });
    }
  }, [debouncedValue]);

  const handleSelect = (item) => {
    setValue(item.domain);
    setShowDropdown(false);
    selectedItem(item);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    if (e.key === "ArrowDown") {
      setActiveIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : prev));
    }

    if (e.key === "ArrowUp") {
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
    }

    if (e.key === "Enter" && activeIndex >= 0) {
      handleSelect(filtered[activeIndex]);
    }
  };

  return (
    <div className="tw-flex-1 relative">
      <TextComboInput
        className="tw-flex-1"
        label="Site"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        // onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => value.length >= 3 && setShowDropdown(true)}
        onBlur={() => setShowDropdown(false)}
        leading={<SearchIcon className="tw-text-neutral-gray-30" size="xl" />}
        trailing={isLoading && <Spinner size="xl" className="tw-text-neutral-gray-30 tw-animate-spin" />}
        placeholder="Enter at least 3 characters"
      />

      {showDropdown && filtered.length > 0 && (
        <div
          role="listbox"
          className="tw-absolute tw-z-10 tw-w-[360px] tw-bg-white tw-border tw-mt-1 tw-rounded-lg tw-shadow-lg tw-max-h-60 tw-overflow-auto"
        >
          {filtered.map((item, index) => (
            <div
              key={item.domain}
              role="option"
              aria-selected={index === activeIndex}
              tabIndex={-1}
              onMouseDown={() => handleSelect(item)}
              className={`tw-px-4 tw-py-2 tw-cursor-pointer ${
                index === activeIndex ? "tw-bg-blue-500 tw-text-white" : "hover:tw-bg-gray-100"
              }`}
            >
              {item.domain}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

Autocomplete.propTypes = {
  selectedItem: PropTypes.func,
};

const PPCAddsDashboard = () => {
  const [siteId, setSiteId] = useState(null);
  const [siteName, setSiteName] = useState(null);
  const [mlsId, setMlsId] = useState(null);
  const [allMlses, setAllMlses] = useState([]);
  const [mlsOptions, setMlsOptions] = useState([]);
  const [groupBy, setGoupBy] = useState("cs");
  const [mlsMetaData, setMlsMetaData] = useState([]);

  useEffect(() => {
    axios.get(`/support/ppc_adds_dashboard/listings_mls`).then((response) => {
      const data = response?.data?.mlses
        ?.map((mls) => {
          return {
            value: mls.mlsId,
            label: mls.name,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      setAllMlses(data);
      setMlsOptions(data);
    });
  }, []);

  useEffect(() => {
    setMlsId(null);
    setMlsMetaData(null);

    if (siteId) {
      axios.get(`/support/ppc_adds_dashboard/account_mls_ids?uuid=${siteId}`).then((response) => {
        const accountMlsIds = allMlses?.filter((mls) => response.data.data.includes(mls.value));
        setMlsOptions(accountMlsIds);
      });
    } else {
      setMlsOptions(allMlses);
    }
  }, [siteId]);

  useEffect(() => {
    if (mlsId) {
      axios
        .get(
          `/support/ppc_adds_dashboard/mls_metadata?mls_ids[]=${mlsId}&field_keys[]=${groupBy}&response_field_set=remo_summary&sort_by=active_count&limit=500`,
        )
        .then((response) => {
          setMlsMetaData(response.data);
        });
    }
  }, [mlsId, groupBy]);

  const groupByOptions = [
    { label: "County", value: "cos" },
    { label: "City", value: "cs" },
    { label: "Zip", value: "zip" },
    { label: "Subdivision", value: "sd" },
    { label: "High School", value: "hs" },
    { label: "Middle School", value: "ms" },
    { label: "Elementry School", value: "es" },
  ];

  const stateMap = {
    AL: "alabama",
    AK: "alaska",
    AZ: "arizona",
    AR: "arkansas",
    CA: "california",
    CO: "colorado",
    CT: "connecticut",
    DE: "delaware",
    FL: "florida",
    GA: "georgia",
    HI: "hawaii",
    ID: "idaho",
    IL: "illinois",
    IN: "indiana",
    IA: "iowa",
    KS: "kansas",
    KY: "kentucky",
    LA: "louisiana",
    ME: "maine",
    MD: "maryland",
    MA: "massachusetts",
    MI: "michigan",
    MN: "minnesota",
    MS: "mississippi",
    MO: "missouri",
    MT: "montana",
    NE: "nebraska",
    NV: "nevada",
    NH: "new-hampshire",
    NJ: "new-jersey",
    NM: "new-mexico",
    NY: "new-york",
    NC: "north-carolina",
    ND: "north-dakota",
    OH: "ohio",
    OK: "oklahoma",
    OR: "oregon",
    PA: "pennsylvania",
    RI: "rhode-island",
    SC: "south-carolina",
    SD: "south-dakota",
    TN: "tennessee",
    TX: "texas",
    UT: "utah",
    VT: "vermont",
    VA: "virginia",
    WA: "washington",
    WV: "west-virginia",
    WI: "wisconsin",
    WY: "wyoming",
    AB: "alberta",
    BC: "british-columbia",
    MB: "manitoba",
    NB: "new-brunswick",
    NL: "newfoundland-and-labrador",
    NS: "nova-scotia",
    NT: "northwest-territories",
    NU: "nunavut",
    ON: "ontario",
    PE: "prince-edward-island",
    QC: "quebec",
    SK: "saskatchewan",
    YT: "yukon",
  };

  const downloadCSV = (csvString, filename) => {
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
  };

  const exportTableToCSV = () => {
    const table = document.querySelector(".ppcTable");
    const rows = table.querySelectorAll("tr");

    const csv = [];

    rows.forEach((row) => {
      const cols = row.querySelectorAll("th, td");
      const rowData = [];

      cols.forEach((col) => {
        if (Array.from(col.children).length < 2) {
          // Clean text and escape quotes
          const data = col?.innerText?.replace(/"/g, '""') || "";
          rowData.push(`"${data}"`);
        } else {
          const data = col.querySelectorAll("p");
          rowData.push(`"${data[0]?.innerHTML?.replace(/"/g, '""') || ""}"`);
        }
      });

      csv.push(rowData.join(","));
    });

    downloadCSV(csv.join("\n"), "ppc_add_data.csv");
  };

  const formatState = (abbrev) => {
    return stateMap[abbrev.toUpperCase()] || null;
  };

  const generateSearchUrl = (row) => {
    const fieldName = row.field_name;
    const fieldValue = row.field_value;

    const site = siteName || "root.com";

    let url = "";
    url = `${site}/search`;

    if (fieldName === "Zip") {
      url += `?multi_search=${fieldValue}&multi_cat=Zip`;
    } else if (fieldName === "City") {
      const cityState = fieldValue.split(",");

      if (cityState.length === 2) {
        const state = formatState(cityState[1].trim());
        const city = cityState[0].trim().toLowerCase().replace(/ /g, "-");

        url += `/${state}/${city}`;
      }
    } else if (fieldName === "Subdivision") {
      const geoContext = row.geo_context;

      const cityState = geoContext?.cs[0]?.split(",");
      const state = formatState(cityState[1]?.trim());
      const city = cityState[0].trim().toLowerCase().replace(/ /g, "-");

      url += `/${state}/${city}/${fieldValue.trim().toLowerCase().replace(/ /g, "-")}`;
    } else if (fieldName === "County") {
      const cityCounty = fieldValue.split(",");

      if (cityCounty.length === 2) {
        const state = cityCounty[1].trim();
        const county = cityCounty[0].trim();

        url += encodeURI(`?multi_search=${county}, ${state}&multi_cat=CountyState`);
      }
    } else if (
      fieldName === "High School" ||
      fieldName === "Middle School" ||
      fieldName === "Elementary School"
    ) {
      url += encodeURI(`?multi_search=${fieldValue}&multi_cat=${fieldName}`);
    }

    return url;
  };

  const openSearchUrl = (data) => {
    const url = generateSearchUrl(data);
    window.open(url, "_blank");
  };

  const formatUSD = (amount) => {
    const number = Number(amount);

    if (Number.isNaN(number)) return "$0";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(number);
  };

  return (
    <div className="user-content">
      <div className="content-wrap">
        <div className="container">
          <div className="row">
            <div className="panel">
              <div className="panel-header">PPC Ads Dashboard</div>
              <div className="panel-body">
                <div className="tw-flex flex-column tw-gap-[64px]">
                  <div className="tw-flex tw-gap-[16px]">
                    <Autocomplete
                      selectedItem={(value) => {
                        setSiteId(value?.organization_uuid || null);
                        setSiteName(value?.domain || null);
                      }}
                    />

                    <Dropdown
                      isSearchable
                      containerClassName="tw-flex-1"
                      menuShouldComeToFront
                      options={mlsOptions}
                      value={mlsOptions.find((o) => o.value === mlsId)}
                      label="MLS"
                      name="mls"
                      // error={validate("role") === "error"}
                      onChange={(o) => setMlsId(o.value)}
                      onFocus={(event) => event.target.select()}
                    />

                    <Dropdown
                      containerClassName="tw-flex-1"
                      menuShouldComeToFront
                      options={groupByOptions}
                      value={groupByOptions.find((o) => o.value === groupBy)}
                      label="Group By"
                      name="groupby"
                      // error={validate("role") === "error"}
                      onChange={(o) => setGoupBy(o.value)}
                    />
                  </div>

                  <div className="tw-flex flex-column tw-gap-[8px] tw-items-end">
                    <Button disabled={mlsId == null} size="small" onClick={() => exportTableToCSV()}>
                      Export to CSV
                    </Button>

                    <Table className="ppcTable">
                      <THead>
                        <TR className="tw-border-x-0">
                          {groupBy === "cos" && (
                            <TH className="tw-border-l-0 tw-py-[8px] tw-font-semibold">County</TH>
                          )}
                          {groupBy === "cs" && (
                            <TH className="tw-border-l-0 tw-py-[8px] tw-font-semibold">City</TH>
                          )}
                          {groupBy === "zip" && (
                            <TH className="tw-border-l-0 tw-py-[8px] tw-font-semibold">Zip</TH>
                          )}
                          {groupBy === "sd" && (
                            <TH className="tw-border-l-0 tw-py-[8px] tw-font-semibold">Subdivision</TH>
                          )}
                          {groupBy === "hs" && (
                            <TH className="tw-border-l-0 tw-py-[8px] tw-font-semibold">High School</TH>
                          )}
                          {groupBy === "ms" && (
                            <TH className="tw-border-l-0 tw-py-[8px] tw-font-semibold">Middle School</TH>
                          )}
                          {groupBy === "es" && (
                            <TH className="tw-border-l-0 tw-py-[8px] tw-font-semibold">Elementary School</TH>
                          )}

                          <TH className="tw-py-[8px] tw-font-semibold">State</TH>
                          <TH className="tw-py-[8px] tw-font-semibold text-center">Listing Count</TH>
                          <TH className="tw-py-[8px] tw-font-semibold text-center">Avg Listing Price</TH>
                          <TH className="tw-py-[8px] tw-font-semibold text-center">Avg Units Sold Monthly</TH>
                          {groupBy === "cs" && (
                            <TH className="tw-border-l-0 tw-py-[8px] tw-font-semibold">County</TH>
                          )}
                          <TH className="tw-py-[8px] tw-font-semibold text-center">Page Link</TH>
                        </TR>
                      </THead>
                      <TBody>
                        {mlsMetaData?.map((data) => (
                          <TR key={data.field_value}>
                            <TD>{data?.field_value?.split(",")[0]}</TD>
                            <TD>
                              {data?.field_value?.split(",")[1] || data?.geo_context?.cos[0]?.split(",")[1]}
                            </TD>
                            <TD className="text-center">{data?.active_count || 0}</TD>
                            <TD className="text-center">
                              {formatUSD(data?.remo_stats?.avg_list_price_active || 0)}
                            </TD>
                            <TD className="text-center">
                              {data.remo_stats?.sold_r6?.avg_closed_per_month || 0}
                            </TD>
                            {groupBy === "cs" && (
                              <TD>
                                {data?.geo_context?.cos?.map((county) => county.split(",")[0])?.join(", ")}
                              </TD>
                            )}
                            <TD className="text-center">
                              <p className="hidden">{generateSearchUrl(data)}</p>
                              <Button disabled={siteId == null} onClick={() => openSearchUrl(data)}>
                                View
                              </Button>
                            </TD>
                          </TR>
                        ))}
                      </TBody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PPCAddsDashboard;
